// src/components/DownloadsPage.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { fetchSoftwareVersions, API_BASE_URL,downloadSoftware } from '../api'; // Assuming API_BASE_URL is exported from api.js
import dayjs from 'dayjs';

function DownloadsPage() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
const [downloadingId, setDownloadingId] = useState(null); // State to track which item is downloading

  useEffect(() => {
    const loadVersions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchSoftwareVersions();
        if (response.code === 0) {
          setVersions(response.data || []);
        } else {
          setError(response.message || 'Failed to load software versions.');
          setVersions([]);
        }
      } catch (err) {
        setError(err.message || 'An unexpected error occurred.');
        setVersions([]);
      } finally {
        setLoading(false);
      }
    };

    loadVersions();
  }, []); // Run only on component mount

  const handleDownload = async (version) => {
    setDownloadingId(version.id); // Set the ID of the version being downloaded
    setError(null); // Clear previous errors

    try {
      await downloadSoftware(version);
    } catch (err) {
      // Set an error message specific to this download failure
      setError(`Failed to download ${version.fileName}: ${err.message}`);
    } finally {
      setDownloadingId(null); // Reset the downloading state
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>;
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Typography variant="h4" gutterBottom>
        Software Downloads
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {versions.length === 0 && !loading  ? (
        <Typography>No software versions available for download.</Typography>
      ) : (
        <List>
          {versions.map((version, index) => (
            <React.Fragment key={version.id}>
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`download ${version.version}`}
                    // The link is wrapped by the IconButton for better click area and styling
                    onClick={() => handleDownload(version)} 
                    disabled={downloadingId === version.id} // Disable while this item is downloading
                  >
                    <DownloadIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`Version ${version.version}`}
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {`Released: ${dayjs(version.releaseDate).format('MMMM D, YYYY')}`}
                      </Typography>
                      {` — ${version.description}`}
                    </>
                  }
                />
              </ListItem>
              {index < versions.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
}

export default DownloadsPage;